// TanStack Query hooks for materializations.
// Ontology spec §6 + plan §1.3.
//
// useMaterializations(dataSourceId?) — list materializations for a data source.
// useMaterialization(id)             — single row with 5s polling while status === 'running'.
// useCreateMaterialization()         — mutation calling the materialize-objects edge function.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { invokeEdgeFunction } from "@/lib/edge-functions";
import type { Materialization } from "@/types/ontology";
import type { MaterializeObjectsRequest, MaterializeObjectsResponse } from "@/types/edge-functions";

// ── Query keys (plan §1.3) ────────────────────────────────────────────────────

export const MATERIALIZATIONS_KEY = ["materializations"] as const;

// ── Queries ───────────────────────────────────────────────────────────────────

/** List all materializations for a data source. */
export function useMaterializations(dataSourceId?: string) {
  const query = useQuery({
    queryKey: dataSourceId ? [...MATERIALIZATIONS_KEY, dataSourceId] : MATERIALIZATIONS_KEY,
    queryFn: async () => {
      let q = supabase
        .from("materializations")
        .select("*")
        .order("started_at", { ascending: false });

      if (dataSourceId) {
        q = q.eq("data_source_id", dataSourceId);
      }

      const { data, error } = await q;

      if (error) throw new Error(error.message);

      return (data as Materialization[]) ?? [];
    },
  });

  return {
    materializations: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

/** Fetch a single materialization row. Polls every 5s while status === 'running'. */
export function useMaterialization(id: string | null) {
  const query = useQuery({
    queryKey: [...MATERIALIZATIONS_KEY, id],
    enabled: id !== null,
    queryFn: async () => {
      if (!id) return null;

      const { data, error } = await supabase
        .from("materializations")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw new Error(error.message);

      return (data as Materialization) ?? null;
    },
    // Poll every 5s while running; stop when terminal state is reached.
    refetchInterval: (query) => {
      const data = query.state.data as Materialization | null;
      if (!data) return false;
      return data.status === "running" ? 5_000 : false;
    },
  });

  return {
    materialization: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}

// ── Mutations ─────────────────────────────────────────────────────────────────

interface CreateMaterializationArgs {
  dataSourceId: string;
  mappingId: string;
}

/**
 * Invoke the materialize-objects edge function and seed the cache with the
 * returned materialization record so polling starts immediately.
 */
export function useCreateMaterialization() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ dataSourceId, mappingId }: CreateMaterializationArgs) => {
      const response = await invokeEdgeFunction<MaterializeObjectsResponse>(
        "materialize-objects",
        { data_source_id: dataSourceId, mapping_id: mappingId } satisfies MaterializeObjectsRequest,
        { idempotent: false }
      );

      return response;
    },
    onSuccess: (_data, variables) => {
      // Invalidate the list so the new materialization row appears.
      queryClient.invalidateQueries({
        queryKey: [...MATERIALIZATIONS_KEY, variables.dataSourceId],
      });
      queryClient.invalidateQueries({ queryKey: MATERIALIZATIONS_KEY });
    },
    onError: (err) => {
      console.error("hook:useCreateMaterialization", err);
      toast({
        title: "Materialization failed",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });
}
